import { getApiUrl } from './api-config';

/**
 * The join link of a commercial invitation (/auth/join?token=...) carries a one-time secret. A person who has to create an account or sign in first loses the page
 * on the way (email confirmation, login), so the token is kept for that round trip only -- in this browser's storage, removed as soon as the invitation is accepted
 * or found invalid -- and the login sends them back to the join page.
 */
const KEY = 'krewup.pendingCommercialInvitation';

export const rememberPendingInvitation = (token: string) => { try { window.localStorage.setItem(KEY, token); } catch { /* storage may be blocked: the email link still works */ } };
export const readPendingInvitation = (): string | null => { try { return window.localStorage.getItem(KEY); } catch { return null; } };
export const forgetPendingInvitation = () => { try { window.localStorage.removeItem(KEY); } catch { /* nothing to do */ } };

export interface InvitationPreview {
  status: 'pending' | 'expired' | 'revoked' | 'not_found';
  kind?: 'commercial_client' | 'commercial_vendor';
  email?: string;
  company_name?: string;
}

export interface AcceptResult {
  status: 'accepted' | 'already_accepted' | 'not_found' | 'expired' | 'revoked' | 'email_not_confirmed' | 'email_mismatch';
  kind?: string;
  account_type?: string | null;
}

async function post<T>(path: string, token: string): Promise<T> {
  const response = await fetch(getApiUrl(path), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) });
  const json = await response.json().catch(() => null);
  if (!response.ok || !json?.success) throw new Error(json?.error || 'The invitation could not be checked');
  return json.data as T;
}

/** Public: what the join page may show before the person has an account. */
export const previewInvitation = (token: string) => post<InvitationPreview>('api/public/commercial-invitations/preview', token);
/** Signed in: accept it. The database checks the confirmed email and the single use. */
export const acceptInvitation = (token: string) => post<AcceptResult>('api/me/commercial-invitations/accept', token);
